import { useHelmet } from '@agro-club/agroclub-shared'
import { isUS } from 'env'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { SlugLabelPair } from 'modules/domain/common/types'
import { ProfileType, User } from 'modules/domain/user/types'
import { getUserName } from 'modules/domain/user/utils'
import { endpoints } from 'modules/endpoints'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ContentWrapper } from 'views/components/Board/styled'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import { TaskModal } from 'views/components/TaskModal/TaskModal'
import * as Layout from 'views/layouts/NewLayout'
import SpinnerLayout from 'views/layouts/SpinnerLayout/SpinnerLayout'
import { BidCreateModal } from 'views/pages/Bid/BidModals/BidCreateModal'
import UserRoutes from 'views/pages/User/routes'
import * as Header from 'views/ui/Header/Header'
import VerifyUserStatus from 'views/ui/VerifyUserStatus/VerifyUserStatus'
import { AnalyticsPages, AnalyticsPlaces } from '../analyticsPlaces'
import { EditUser } from '../ModalUser/EditUser'
import { Board } from './Board/Board'
import { HeaderControls } from './HeaderControls'
import { HeaderSubTitleValue } from './HeaderSubTitleValue'
import { USBoard } from './USBoard/USBoard'
import { UserRightBlock } from './UserRightPanel/UserRightBlock'
import { useUserTasksManager } from './UserRightPanel/useUserTasksManager'
import { useUserBids } from './useUserBids'
import { UserSamplesProvider } from './UserSamplesContext'

export function UserDetail() {
  const { id } = useParams()
  const { t } = useTranslation('user')
  const [, user, userRefetch] = useSingleEntity<User>(endpoints.getUserById(id))
  const taskDataManager = useUserTasksManager(id)
  const userBids = useUserBids(id)

  const [, reasons] = useSingleEntity<SlugLabelPair[]>(endpoints.dealsCloseReasons())
  const [editUserModal, setEditUserModal] = useState<boolean>(false)
  useHelmet({ title: user?.profile?.full_name || user?.phone })
  const [taskModal, setTaskModal] = useState<boolean>(false)
  const [bidModal, setBidModal] = useState<boolean>(false)

  const addBidModalTitle =
    user && user.profile?.profile_type === ProfileType.customer ? t('bid:form.buyerAddBid') : t('bid:form.add')

  if (!user) {
    return <SpinnerLayout />
  }

  const onSuccessTaskModal = async () => {
    if (user.profile?.do_not_call_reason) {
      await userRefetch()
    }
    await taskDataManager.refetch()
  }

  return (
    <APageWrapper page={AnalyticsPages.USER_DETAIL} place={AnalyticsPlaces.DETAIL} id={id}>
      <UserSamplesProvider userId={parseInt(id)}>
        <Layout.WrapperContent>
          <Layout.Header>
            <Layout.TopHeader>
              <Header.Breadcrumbs
                routes={[
                  { path: UserRoutes.List, breadcrumbName: t('list.heading') },
                  { breadcrumbName: user.profile?.full_name || '' },
                ]}
              />
              <HeaderControls
                setBidModal={setBidModal}
                setEditUserModal={setEditUserModal}
                setTaskModal={setTaskModal}
                user={user}
              />
            </Layout.TopHeader>
          </Layout.Header>

          <Header.Title margin="0" compact title={getUserName(user)}>
            <VerifyUserStatus user={user} />
          </Header.Title>
          <Header.SubTitle>
            <HeaderSubTitleValue user={user} />
          </Header.SubTitle>
          {!reasons ? (
            <SpinnerLayout />
          ) : (
            <ContentWrapper>
              {isUS ? <USBoard userId={user.id} reasons={reasons} /> : <Board reasons={reasons} bids={userBids} />}
              <UserRightBlock
                bidsRefetch={userBids.refetch}
                taskManager={taskDataManager}
                userRefetch={userRefetch}
                user={user}
                openTaskModal={() => setTaskModal(true)}
              />
            </ContentWrapper>
          )}

          {editUserModal && (
            <EditUser user={user} onClose={(status) => setEditUserModal(status)} userRefetch={userRefetch} />
          )}
          {taskModal && (
            <TaskModal
              unclickableUserLink
              onSuccess={onSuccessTaskModal}
              user={user}
              close={() => setTaskModal(false)}
              skipOnSuccessBeforeRedirect
            />
          )}
          {bidModal && <BidCreateModal formTitle={addBidModalTitle} user={user} onClose={() => setBidModal(false)} />}
        </Layout.WrapperContent>
      </UserSamplesProvider>
    </APageWrapper>
  )
}
